import { useEffect, useState } from 'react';
import { api, type CreditoDaCliente, type LinhaDoCredito } from '../api';
import { voltar } from '../rota';
import { Carregando } from '../../../compartilhado/Espera';
import { MESES, diaCurto } from '../formato';

/**
 * O crédito (cashback) da cliente — a F9.
 *
 * ⚠️ **Existe porque o app nunca soube de cashback.** Ele é inteiro sobre
 * moeda e cartela: não havia saldo, nem extrato, nem tela. Até 13/09 o único
 * lugar onde ela conseguia perguntar era o WhatsApp.
 *
 * ⚠️ **Crédito não é moeda, e a tela não deixa confundir.** Moeda vira prêmio;
 * crédito vira desconto em reais. A palavra "moeda" não aparece aqui, e a
 * tela de moedas não fala em crédito — trocar os nomes confunde no caixa, que
 * é onde a confusão custa.
 *
 * ⚠️ **Disponível e em carência nunca se somam.** Prometer dinheiro que ela
 * ainda não pode usar é gerar uma cliente brava no balcão — a mesma regra do
 * saldo de moedas (§ carência).
 */

const brl = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/* O que cada linha do extrato é, em palavras da cliente. O motivo vem escrito
   de quem lançou ("abatido no caixa"), e é ele que manda quando existe. */
function rotulo(l: LinhaDoCredito): string {
  if (l.motivo?.trim()) return l.motivo;
  return l.tipo === 'entrada' ? 'crédito da compra' : 'usado na compra';
}

export function Credito({ aoFalhar }: { aoFalhar: (e: unknown) => void }) {
  const [dados, setDados] = useState<CreditoDaCliente | null>(null);
  const [erro, setErro] = useState('');

  useEffect(() => {
    api
      .cashback()
      .then(setDados)
      .catch((e) => {
        setErro('não consegui carregar seu crédito');
        aoFalhar(e);
      });
  }, [aoFalhar]);

  if (erro) return <div className="erro-tela">{erro}</div>;
  if (!dados)
    return (
      <div className="espera-tela">
        <Carregando />
      </div>
    );

  const topo = (
    <header className="ex-topo">
      <button className="volta" onClick={voltar} aria-label="voltar">
        <svg width="16" height="16" viewBox="0 0 16 16">
          <path
            d="M10 3 5 8l5 5"
            fill="none"
            stroke="var(--ink)"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <h1 className="titulo-tela sem-margem">seu crédito</h1>
    </header>
  );

  /* A loja desligou o cashback depois que a cliente abriu o app, ou ela chegou
     aqui por um link velho: diz, em vez de mostrar zero como se fosse saldo. */
  if (!dados.usa) {
    return (
      <div className="tela ex">
        {topo}
        <div className="ex-vazio">
          <p>esta loja não trabalha com crédito</p>
        </div>
      </div>
    );
  }

  const grupos: { mes: string; linhas: LinhaDoCredito[] }[] = [];
  for (const l of dados.linhas) {
    const d = new Date(l.dia);
    const ano = d.getFullYear() !== new Date().getFullYear() ? ` ${d.getFullYear()}` : '';
    const mes = `${MESES[d.getMonth()]}${ano}`;
    const grupo = grupos[grupos.length - 1];
    if (grupo && grupo.mes === mes) grupo.linhas.push(l);
    else grupos.push({ mes, linhas: [l] });
  }

  return (
    <div className="tela ex">
      {topo}

      <div className="cr-saldo">
        <span className="cr-numero">{brl(dados.disponivel)}</span>
        <span className="cr-legenda">para usar na próxima compra</span>
      </div>

      {/* ⚠️ À PARTE, nunca somado ao de cima. */}
      {dados.bloqueado > 0 && (
        <div className="cr-bloqueado">
          <b>{brl(dados.bloqueado)}</b> ainda esperando
          {dados.liberaEm && <> · libera {diaCurto(dados.liberaEm)}</>}
        </div>
      )}

      <div className="cr-regra">
        {dados.noCaixa ? (
          <>
            O desconto sai <b>sozinho no caixa</b> quando você for pagar.
          </>
        ) : (
          <>
            Peça na loja para usar seu crédito: quem abate é o <b>atendente</b>, na hora
            de fechar a conta.
          </>
        )}
        {dados.minimoUso > 0 && (
          <>
            {' '}
            Vale em compras a partir de <b>{brl(dados.minimoUso)}</b>.
          </>
        )}
        {dados.maximoUso > 0 && (
          <>
            {' '}
            Dá para pagar até <b>{dados.maximoUso}%</b> de uma compra com crédito.
          </>
        )}
      </div>

      {dados.linhas.length === 0 ? (
        <div className="ex-vazio">
          <p>seu primeiro crédito aparece aqui</p>
        </div>
      ) : (
        grupos.map((g) => (
          <section key={g.mes}>
            <div className="ex-mes">{g.mes}</div>
            {g.linhas.map((l, i) => (
              <div key={i} className="ex-linha">
                <span className="ex-meio">
                  <span className="ex-titulo">{rotulo(l)}</span>
                  <span className="ex-sub">{diaCurto(l.dia)}</span>
                </span>
                <span className={l.tipo === 'entrada' ? 'cr-mais' : 'cr-menos'}>
                  {l.tipo === 'entrada' ? '+' : '−'}
                  {brl(l.valor)}
                </span>
              </div>
            ))}
          </section>
        ))
      )}
    </div>
  );
}

/**
 * O cartão do crédito na carteira — a porta de entrada desta tela.
 *
 * ⚠️ **Busca o próprio dado, e some quando não há.** Podia vir dentro de
 * `/carteira`, e não vem de propósito: a carteira é da fidelidade, o crédito é
 * de outro módulo (CLAUDE.md), e a loja pode ter um sem o outro. Buscando
 * sozinho, ele aparece igual na tela de moedas e na de cartela, sem que
 * nenhuma das duas precise saber que ele existe.
 *
 * Custo: um pedido a mais na primeira tela. Paga-se com a separação — e ele
 * falha sozinho, sem derrubar a carteira, que é o que a cliente veio ver.
 */
export function CartaoDeCredito({ ir }: { ir: (p: string) => void }) {
  const [dados, setDados] = useState<CreditoDaCliente | null>(null);

  useEffect(() => {
    /* Falhar aqui não é notícia para a cliente: o cartão simplesmente não
       aparece, e a carteira continua de pé. */
    api
      .cashback()
      .then(setDados)
      .catch(() => setDados({ usa: false }));
  }, []);

  if (!dados?.usa) return null;
  /* Sem crédito nenhum e sem nada esperando, o cartão seria uma linha dizendo
     "R$ 0,00" — ruído na tela que ela abre para ver o que tem. */
  if (dados.disponivel <= 0 && dados.bloqueado <= 0) return null;

  return (
    <button className="ca-credito" onClick={() => ir('/credito')}>
      <span className="ca-credito-valor">{brl(dados.disponivel)}</span>
      <span className="ca-credito-legenda">
        de crédito
        {dados.bloqueado > 0 && <> · {brl(dados.bloqueado)} a caminho</>}
      </span>
    </button>
  );
}
